import { Button, Box } from '@mui/material';
import {
    GridValueGetterParams,
    DataGridPro,
    DataGridProProps,
    GridColDef,
    GridRenderCellParams,
    GridToolbar,
} from '@mui/x-data-grid-pro';
import React from 'react';
import { NavLink } from 'react-router-dom';

import { RequestStatus } from 'app/components/request-status';
import { BusinessImpactEnumDict, OverdueImpactEnumDict, PriorityEnumDict } from 'app/enum';
import { CrisRequest } from 'app/model';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

interface CrisListGridProps extends Omit<DataGridProProps<CrisRequest>, 'columns'> {
    rows: CrisRequest[];
    openTarget?: string;
}

export const CrisListGrid: React.FunctionComponent<CrisListGridProps> = (props) => {
    const columns: Array<GridColDef<CrisRequest>> = [
        {
            field: 'open',
            headerName: '',
            width: 80,
            renderCell: (params) => (
                <Button component={NavLink} to={`/cris/request/${params.row.id}`} target={props.openTarget}>
                    Open
                </Button>
            ),
            filterable: false,
        },
        {
            field: 'id',
            headerName: 'ID',
            width: 300,
        },
        {
            field: 'brand',
            headerName: 'Linked Brand',
            width: 150,
            valueGetter: (params: GridValueGetterParams<CrisRequest, CrisRequest['brand']>) =>
                params.value?.name ?? '[None]',
        },
        {
            field: 'elementName',
            headerName: 'Requested Brand',
            width: 150,
        },
        {
            field: 'customer',
            headerName: 'Customer',
            width: 150,
        },
        {
            field: 'project',
            headerName: 'Project',
            width: 150,
        },
        {
            field: 'taggerPriority',
            headerName: 'Tagging Priority',
            width: 80,
            valueGetter: (params: GridValueGetterParams<CrisRequest, CrisRequest['taggerPriority']>) =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                PriorityEnumDict[params.value!],
        },
        {
            field: 'priority',
            headerName: 'Priority',
            width: 80,
            valueGetter: (params: GridValueGetterParams<CrisRequest, CrisRequest['taggerPriority']>) =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                PriorityEnumDict[params.value!],
        },
        {
            field: 'currentPlace',
            headerName: 'Status',
            width: 130,
            renderCell: (params: GridRenderCellParams<CrisRequest, CrisRequest['currentPlace']>) => (
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                <RequestStatus status={params.value!} />
            ),
        },
        {
            field: 'dueDate',
            headerName: 'Due By',
            type: 'date',
            width: 110,
            valueFormatter: DataGridFormatters.dateFormatter,
        },
        {
            field: 'estimatedDelivery',
            headerName: 'ETA',
            type: 'date',
            width: 110,
            valueFormatter: DataGridFormatters.dateFormatter,
        },
        {
            field: 'businessImpact',
            headerName: 'Business Impact',
            width: 170,
            valueGetter: (params: GridValueGetterParams<CrisRequest, CrisRequest['businessImpact']>) =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                BusinessImpactEnumDict[params.value!],
        },
        {
            field: 'overdueImpact',
            headerName: 'Overdue Impact',
            width: 150,
            valueGetter: (params: GridValueGetterParams<CrisRequest, CrisRequest['overdueImpact']>) =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                OverdueImpactEnumDict[params.value!],
        },
        {
            field: 'dateCreated',
            type: 'dateTime',
            headerName: 'Created',
            width: 200,
            valueFormatter: DataGridFormatters.dateTimeFormatter,
        },
        {
            field: 'userCreated',
            headerName: 'Request By',
            width: 250,
            valueGetter: DataGridFormatters.usernameFormatter,
        },
    ];

    return (
        <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
            <DataGridPro
                components={{ Toolbar: GridToolbar }}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                    sorting: {
                        sortModel: [{ field: 'dateCreated', sort: 'desc' }],
                    },
                }}
                {...props}
                columns={columns}
            />
        </Box>
    );
};
